import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect, useSelector } from "react-redux";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"; // Import resolver
import { commonStyles } from "../AuthenticationLayout/authenticationLayoutCommonStyling.tailwind.js";
import Button from "../shared/Button/Button.jsx";
import routeConstant, { REDIRECT_ON_LOGIN } from "../../lib/constants/routeConstant.js";
import InputController from "../shared/Input/InputField/InputController.jsx";
import PhoneAndEmailField from "../shared/Input/PhoneAndEmailField/PhoneAndEmailField.jsx";
import { loginWithPasswordSchema, loginWithOTPScheama } from "./login.validation.js";
import { AUTHENTICATION_METHOD, VIA_EMAIL } from "../../lib/constants/globalConstant.js";
import pathConstant from "../../lib/constants/pathConstant.js";
import { phoneNumberRegex } from "../../lib/constants/regexConstant.js";
import { login } from "../../redux/slices/authSlice.js";
import GoogleAuth from "../shared/Button/googleAuthButton.jsx";
import { storage } from "../../config/storage.js";
import OverlayLoader from "../shared/Loaders/OverlayLoader/OverlayLoader.jsx";
// import ProgressBar from "../CreatePassword/ProgressBar.jsx";

const LoginForm = ( { getSignedIn }) => {
  const navigate = useNavigate()
  const { isLoading: isLogging } = useSelector(state => state.auth)
  const { loggingWithGoogle } = useSelector(state => state.googleAuth)

  // FOR DEMO (CHANGE BACK IT AUTHENTICATION_MENTHOD.PASSWORD) TO AUTHENTICATION_METHOD.OTP)
  const [signInMethod, setSignInMethod] = useState(AUTHENTICATION_METHOD.PASSWORD)

  const validationSchema = signInMethod === AUTHENTICATION_METHOD.PASSWORD ? loginWithPasswordSchema : loginWithOTPScheama
  // controller and state handled by react-router-dom
  const { control, setValue, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(validationSchema), // Use Yup resolver
  });

  // Check if the input value is a phone number (digits only)
  const inputValue = useWatch({ control, name: "value" }) || "";
  const [isPhoneInputVisible, setIsPhoneInputVisible] = useState(phoneNumberRegex.test(inputValue));

  // handle login user
  const loginUser = async (formData) => {
  
    /* state has been passed so that the redirection to the next page can be handled accordingly. And the data 
       can be fetched from the state and sent to the api */
    const state = {
      stateValue: isPhoneInputVisible ? `+${formData.value}` : formData.value, // value of the input field
      password: formData.password,
      signInMethod, // If password login, send it as `password` else send it as `otp`
      loginViaMethod: isPhoneInputVisible // If phone, send it as `phoneNumber` else send it as `email`
        ? VIA_EMAIL.PHONE_NUMBER
        : VIA_EMAIL.EMAIL,
      from: pathConstant[routeConstant.LOGIN_PAGE], // tells the path from where the user came from
    };

    // data passed to api
    const data = isPhoneInputVisible  // if phone input field is visible then phone number else email is sent password, viaEmail and loginVia are sent
      ? {
          password: formData.password,
          phoneNumber: `+${formData.value}`, 
          viaEmail: VIA_EMAIL.PHONE_NUMBER,
          loginVia: signInMethod,
        }
      : {
          password: formData.password,
          email: formData.value,
          viaEmail: VIA_EMAIL.EMAIL,
          loginVia: signInMethod,
        };

    const resultAction = await getSignedIn(data);
    if (login.fulfilled.match(resultAction)) { // If the login action is fulfilled(means successful)
      if (signInMethod === AUTHENTICATION_METHOD.OTP) {
        navigate(`${routeConstant.VERIFY_OTP_PAGE}`, { state }); // If OTP login, navigate to OTP verification page
      } else {
        // check if we have some route to navigate from local-storage
        const defaultNavigation = storage.fetchFromLocalStorage.navigateOnLogin();
        // if we have no route to navigate from local-storage then navigate to default route
        const navigateToRoute = defaultNavigation ? defaultNavigation : REDIRECT_ON_LOGIN;

        navigate(navigateToRoute); // If password login, navigate to appropriate page

        // now clear from local storage
        storage.destroyFromLocalStorage.navigateOnLogin();
      }
    }
  };

  // if(isLogging || loggingWithGoogle) return <OverlayLoader />
  return (
    <div className={commonStyles.sub_container}>
      {(isLogging || loggingWithGoogle) && <OverlayLoader /> }
      {/* Login Form */}
      <form className={commonStyles.form_wrpr} onSubmit={handleSubmit(loginUser)}>
        {/* Input Fields */}
        <div className={commonStyles.input_fields_wrpr}>
          {/* Email or Phone Number Input componet handled by PhoneAndEmailField */}
          <PhoneAndEmailField
            name="value"
            control={control}
            setValue={setValue}
            inputValue={inputValue}
            isPhoneInputVisible={isPhoneInputVisible}
            setIsPhoneInputVisible={setIsPhoneInputVisible}
            error={!!errors.value}
            helperText={errors.value?.message}
          />

          {/* Password Field */}
          {signInMethod ===AUTHENTICATION_METHOD.PASSWORD && (
            // only if login via password
            <InputController
              label="Password"
              type="password"
              name="password"
              control={control}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
          )}

          {/* Forgot Password Link */}
          <div className={`${commonStyles.forgot_password}`}>
            {signInMethod === AUTHENTICATION_METHOD.PASSWORD ? (
              // only if login via password
              //FOR DEMO UNCOMMENT THE DIV AND remove Empty fragments
              // <div className={commonStyles.pageLinks} onClick={() => {
              //   setSignInMethod(AUTHENTICATION_METHOD.OTP);
              // }}>
              //   Login using OTP?
              // </div>
              <></>
            ) : (
              // only if login via otp
              <div className={`ml-auto ${commonStyles.pageLinks}`} onClick={() => {
                setSignInMethod(AUTHENTICATION_METHOD.PASSWORD);
                setValue( 'password', ""); // will reset-password on changing button
              }}>
                Login using Password?
              </div>
            )}

            {signInMethod === AUTHENTICATION_METHOD.PASSWORD && <Link to={routeConstant.FORGOT_PASSWORD_PAGE} tabIndex={-1} className={commonStyles.pageLinks}>Forgot Password?</Link>}
          </div>
        </div>

        {signInMethod === AUTHENTICATION_METHOD.OTP ? (
          // Login via Password
          <Button variant="primary" type="submit" disabled={isLogging || loggingWithGoogle} className={commonStyles.loginButton}>{isLogging ? `Logging...` : `Login with OTP`}</Button>          
        ) : (
          // Login via OTP
          <Button variant="primary" type="submit" disabled={isLogging || loggingWithGoogle} className={commonStyles.loginButton}>{isLogging ? `Logging...` : `Login`}</Button>          
        )}
      </form>

      {/* Bottom-Divider/Content */}
      <div className={commonStyles.bottom_cntnt}>
        {/* Divider with "or" */}
        <div className={commonStyles.orContainer}>
          <div className={commonStyles.orLines}></div> {/* Line before "or" */}
          <span>or</span> {/* Centered "or" */}
          <div className={commonStyles.orLines}></div> {/* Line after "or" */}
        </div>

        {/* Login with Google Button */}
        <GoogleAuth text={"Login with Google"} />

        {/* Sign up prompt */}
        <Link to={routeConstant.SIGNUP_PAGE} className="text-center text-[14px] sm:text-[16px]">
          Don&apos;t have an account?{" "}
          <span className={commonStyles.pageLinks}>Sign Up</span>
        </Link>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  getSignedIn: (data) => dispatch(login(data)),
});

export default connect(null, mapDispatchToProps)(LoginForm);